import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { SetStateAction, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { File as CustomFile } from "@/types";
import { v4 as uuidv4 } from "uuid";
import { LuX } from "react-icons/lu";
import Lottie from "lottie-react";
import uploadingLoader from "@/assets/uploading_loader.json";
import { UploadingFile } from "./types";
import {
  SqTooltip,
  SqTooltipTrigger,
  SqTooltipContent,
  SqTooltipProvider,
} from "@/components/squircle-ui/tooltip";
import { withCacheBuster } from "@/components/common/adCard/Media";

const isVideoFile = (fileName: string | undefined) => {
  if (!fileName) return false;
  return /(\.mp4$|\.webm$)/i.test(fileName);
};

const FileAttachmentsPreview = ({
  files,
  className = "",
  onRemove,
  uploadingFiles = [],
  showFileName,
}: {
  files: CustomFile[];
  className?: string;
  onRemove?: (file: CustomFile) => void;
  uploadingFiles?: UploadingFile[];
  showFileName?: boolean;
}) => {
  const [preview, setPreview] = useState<{ file: CustomFile; layoutId: string } | null>(
    null,
  );
  const [layoutTrigger, setLayoutTrigger] = useState(0);
  const [isPreviewLoaded, setIsPreviewLoaded] = useState(false);

  return (
    <>
      <div
        className={cn(
          `flex flex-nowrap items-center gap-2 overflow-x-auto overflow-y-hidden scrollbar-hide pb-2`,
          className,
        )}
      >
        {files.map((file) => (
          <FileThumbnail
            key={file.id}
            setPreview={(preview) => {
              setLayoutTrigger(Date.now());
              setPreview(preview);
              setIsPreviewLoaded(false);
            }}
            file={file}
            onRemove={onRemove}
            layoutTrigger={layoutTrigger}
            showFileName={showFileName}
          />
        ))}
        {uploadingFiles.map((item) => (
          <UploadingFileThumbnail key={item.id} uploadingItem={item} />
        ))}
      </div>

      {/* Fullscreen Preview - Rendered via Portal to document.body */}
      {createPortal(
        <AnimatePresence>
          {preview && (
            <>
              {/* Overlay */}
              <motion.div
                key="overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setLayoutTrigger(Date.now());
                  setPreview(null);
                  setIsPreviewLoaded(false);
                }}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
                style={{ pointerEvents: "auto" }}
              />

              {/* Enlarged Preview */}
              <motion.div
                key="previewWrapper"
                className="fixed inset-0 z-[60] flex items-center justify-center pointer-events-none"
              >
                <motion.div
                  className="w-fit relative pointer-events-auto"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {/* overlay for file name  */}
                  {preview && showFileName && isPreviewLoaded && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className={`absolute flex top-0 rounded-t-[6px] bg-gradient-to-b from-black/50 to-black/0 items-start h-16 px-3 py-2 w-full z-50`}
                    >
                      <motion.span className="text-white text-[12px] font-normal leading-[14px] tracking-[-0.1px] truncate overflow-hidden whitespace-nowrap w-full block px-1 py-1">
                        {preview.file.file_name}
                      </motion.span>
                    </motion.div>
                  )}
                  {isVideoFile(preview.file.public_url) ? (
                    <motion.video
                      src={preview.file.public_url}
                      layoutId={preview.layoutId}
                      className="max-h-[80vh] max-w-[80vw] object-contain rounded-md pointer-events-auto"
                      controls
                      onLoadedData={() => setTimeout(() => setIsPreviewLoaded(true), 400)}
                      onLoadStart={() => setIsPreviewLoaded(false)}
                    />
                  ) : (
                    <motion.img
                      crossOrigin="anonymous"
                      src={withCacheBuster(preview.file.public_url)}
                      layoutId={preview.layoutId}
                      className="max-h-[80vh] max-w-[80vw] object-contain rounded-md pointer-events-auto"
                      onLoadStart={() => setIsPreviewLoaded(false)}
                      onLoad={() => setTimeout(() => setIsPreviewLoaded(true), 400)}
                    />
                  )}
                </motion.div>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
};

const FileThumbnail = ({
  file,
  setPreview,
  onRemove,
  layoutTrigger,
  showFileName,
}: {
  file: CustomFile;
  setPreview: React.Dispatch<
    SetStateAction<{ file: CustomFile; layoutId: string } | null>
  >;
  onRemove?: (file: CustomFile) => void;
  layoutTrigger: number;
  showFileName?: boolean;
}) => {
  const isVideo = isVideoFile(file.public_url);
  const layoutId = useMemo(() => uuidv4(), []);
  const [hasLoadError, setHasLoadError] = useState(false);

  // Don't render if the file failed to load
  if (hasLoadError) return null;

  return (
    <SqTooltipProvider>
      <SqTooltip>
        <SqTooltipTrigger asChild>
          <div
            key={layoutId}
            className="relative group flex-none cursor-pointer"
            data-media-url={file.public_url}
            onClick={() => setPreview({ file, layoutId })}
          >
            {/* overlay for file name */}
            {showFileName && (
              <div className="absolute opacity-0 group-hover:opacity-100 inset-0 bg-black/60 grid rounded-[8px] place-items-end pointer-events-none group-hover:pointer-events-auto">
                <span className="text-white text-[10px] font-normal leading-[14px] tracking-[-0.1px] truncate overflow-hidden whitespace-nowrap w-full block px-1 py-1">
                  {file.file_name}
                </span>
              </div>
            )}

            {/* Preview Image / Video */}
            {isVideo ? (
              <motion.video
                src={file.public_url}
                layoutId={layoutId}
                layoutDependency={layoutTrigger}
                className="h-[80px] w-[80px] rounded-[8px] border-[1px] border-[#E7E9EF] object-cover flex-shrink-0 pointer-events-none"
                style={{ aspectRatio: "1/1" }}
                muted
                loop
                playsInline
                onError={() => setHasLoadError(true)}
              />
            ) : (
              <motion.img
                crossOrigin="anonymous"
                src={withCacheBuster(file.public_url)}
                layoutId={layoutId}
                layoutDependency={layoutTrigger}
                className="h-[80px] w-[80px] rounded-[8px] border-[1px] border-[#E7E9EF] object-cover flex-shrink-0 pointer-events-none"
                style={{ aspectRatio: "1/1" }}
                onError={() => setHasLoadError(true)}
              />
            )}
            {/* Remove Button (shown on hover or always if desired) */}
            {onRemove && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(file);
                }}
                className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-[#09090B80] shadow-sm flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-auto"
              >
                <LuX size={12} />
              </button>
            )}
          </div>
        </SqTooltipTrigger>
        <SqTooltipContent side="top" align="end" portal={true}>
          {file.file_name}
        </SqTooltipContent>
      </SqTooltip>
    </SqTooltipProvider>
  );
};

const UploadingFileThumbnail = ({ uploadingItem }: { uploadingItem: UploadingFile }) => {
  const isVideo = isVideoFile(uploadingItem.file.name);
  const layoutId = useMemo(() => uuidv4(), []);

  return (
    <div key={layoutId} className="relative group flex-none">
      {/* Preview Image / Video */}
      {isVideo ? (
        <video
          src={uploadingItem.preview}
          className="h-[60px] w-[60px] rounded-[8px] border-[1px] border-[#E7E9EF] object-cover flex-shrink-0"
          style={{ aspectRatio: "1/1" }}
          muted
          loop
          playsInline
        />
      ) : (
        <img
          src={uploadingItem.preview}
          className="h-[60px] w-[60px] rounded-[8px] border-[1px] border-[#E7E9EF] object-cover flex-shrink-0"
          style={{ aspectRatio: "1/1" }}
          alt={uploadingItem.file.name || "uploading file"}
        />
      )}

      {/* Upload overlay */}
      <div className="absolute inset-0 bg-white/40 grid place-items-center backdrop-blur-[4px] rounded-[8px]">
        <Lottie
          animationData={uploadingLoader}
          loop
          autoplay
          style={{ transform: "scale(2.8)", position: "relative", top: 5, left: 6 }}
        />
      </div>
    </div>
  );
};

export default FileAttachmentsPreview;
